class CartDrawer extends DrawerComponent {
  constructor() {
    super();
    window.FoxKitSections = FoxTheme.utils.getSectionId(this);

    this.onCartRefreshListener = this.onCartRefresh.bind(this);
    this.getSectionToRenderListener = this.getSectionToRender.bind(this);
  }

  get requiresBodyAppended() {
    return false;
  }

  get sectionId() {
    return this.getAttribute("data-section-id");
  }

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(
      "cart:grouped-sections",
      this.getSectionToRenderListener
    );
    document.addEventListener("cart:refresh", this.onCartRefreshListener);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(
      "cart:grouped-sections",
      this.getSectionToRenderListener
    );
    document.removeEventListener("cart:refresh", this.onCartRefreshListener);
  }

  getSectionToRender(event) {
    event.detail.sections.push(FoxTheme.utils.getSectionId(this));
  }

  show(focusElement = null, animate = true) {
    super.show(focusElement, animate);

    if (this.open) {
      FoxTheme.a11y.trapFocus(this, this.focusElement);
    }
  }

  async onCartRefresh(event) {
    const cartId = `CartDrawer-${this.sectionId}`;

    const cartElement = document.getElementById(cartId);
    if (!cartElement) return;

    try {
      const response = await fetch(
        `${FoxTheme.routes.root_url}?section_id=${this.sectionId}`
      );
      const responseText = await response.text();

      const parser = new DOMParser();
      const parsedHTML = parser.parseFromString(responseText, "text/html");

      const newCartContent = parsedHTML.getElementById(cartId).innerHTML;
      cartElement.innerHTML = newCartContent;

      if (event.detail.open === true) {
        this.show();
      }
    } catch (error) {
      console.error("Error refreshing cart:", error);
    }
  }
}
customElements.define("cart-drawer", CartDrawer);

class CartItems extends HTMLElement {
  constructor() {
    super();

    this.addEventListener(
      "change",
      FoxTheme.utils.debounce(this.onChange.bind(this), 300)
    );
    this.cartUpdateUnsubscriber = FoxTheme.pubsub.subscribe(
      FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate,
      this.onCartUpdate.bind(this)
    );

    this.cartItemProducts = this.querySelectorAll(".cart-item__product");
    this.cartItemQuantitys = this.querySelectorAll(".cart-item__quantity");

    const loadTemplateContent = (elements, parentSelector) => {
      elements.forEach((element) => {
        const template = element.querySelector("template");
        const templateContent =
          template && document.importNode(template.content, true);
        const parent = element.querySelector(parentSelector);

        if (parent && template && !parent.querySelector(".template-content")) {
          const contentWrapper = document.createElement("div");
          contentWrapper.classList.add("template-content", "w-full");
          contentWrapper.appendChild(templateContent);
          parent.appendChild(contentWrapper);
        }
      });
    };

    const removeTemplateContent = (elements, parentSelector) => {
      elements.forEach((element) => {
        const parent = element.querySelector(parentSelector);
        const templateContent = parent?.querySelector(".template-content");

        if (templateContent) {
          parent.removeChild(templateContent);
        }
      });
    };

    const handleTabletMatch = () => {
      loadTemplateContent(this.cartItemProducts, ".cart-item__product--info");
      removeTemplateContent(
        this.cartItemQuantitys,
        ".cart-item__quantity-wrapper"
      );
    };

    const handleTabletUnmatch = () => {
      removeTemplateContent(this.cartItemProducts, ".cart-item__product--info");
      loadTemplateContent(
        this.cartItemQuantitys,
        ".cart-item__quantity-wrapper"
      );
    };

    const mqlTablet = window.matchMedia(FoxTheme.config.mediaQueryTablet);
    FoxTheme.config.mqlTablet = mqlTablet.matches;

    if (FoxTheme.config.mqlTablet) {
      handleTabletMatch();
    } else {
      handleTabletUnmatch();
    }

    mqlTablet.onchange = (event) => {
      if (event.matches) {
        handleTabletMatch();
      } else {
        FoxTheme.config.mqlTablet = false;
        handleTabletUnmatch();
      }
    };

    window.FoxKitSections = FoxTheme.utils.getSectionId(this);
  }

  cartUpdateUnsubscriber = undefined;

  disconnectedCallback() {
    if (this.cartUpdateUnsubscriber) {
      this.cartUpdateUnsubscriber();
    }
  }

  onChange(event) {
    this.validateQuantity(event);
  }

  setValidity(target, message) {
    target.setCustomValidity(message);
    target.reportValidity();
    target.value = target.defaultValue;
    target.select();
  }

  validateQuantity(event) {
    const target = event.target;
    const inputValue = parseInt(target.value);
    const index = target.getAttribute("data-index");
    let message = "";

    if (inputValue < parseInt(target.getAttribute("data-min"))) {
      message = FoxTheme.quickOrderListStrings.min_error.replace(
        "[min]",
        target.getAttribute("data-min")
      );
    } else if (inputValue > parseInt(target.max)) {
      message = FoxTheme.quickOrderListStrings.max_error.replace(
        "[max]",
        target.max
      );
    } else if (inputValue % parseInt(target.step) !== 0) {
      message = FoxTheme.quickOrderListStrings.step_error.replace(
        "[step]",
        target.step
      );
    }

    if (message) {
      this.setValidity(target, message);
    } else {
      target.setCustomValidity("");
      target.reportValidity();
      this.updateQuantity(
        index,
        inputValue,
        document.activeElement.getAttribute("name"),
        target
      );
    }
  }

  onCartUpdate(event) {
    if (event.cart.errors) {
      this.onCartError(event.cart.errors, event.target);
      return;
    }

    const sectionId = FoxTheme.utils.getSectionId(this);
    const sectionToRender = new DOMParser().parseFromString(
      event.cart.sections[sectionId],
      "text/html"
    );

    const cartDrawer = document.querySelector(`#CartDrawer-${sectionId}`);
    const cartDrawerBody = document.querySelector(
      `#CartDrawerBody-${sectionId}`
    );
    const cartDrawerFooter = document.querySelector(
      `#CartDrawerFooter-${sectionId}`
    );
    const cartDrawerEmpty = document.querySelector(
      `#CartDrawerEmpty-${sectionId}`
    );
    if (cartDrawer) {
      const cartDrawerBodyUpdate = sectionToRender.querySelector(
        `#CartDrawerBody-${sectionId}`
      );
      const cartDrawerFooterUpdate = sectionToRender.querySelector(
        `#CartDrawerFooter-${sectionId}`
      );
      const cartDrawerEmptyUpdate = sectionToRender.querySelector(
        `#CartDrawerEmpty-${sectionId}`
      );

      if (cartDrawerBodyUpdate) {
        cartDrawerBody.innerHTML = cartDrawerBodyUpdate.innerHTML;
      }
      if (cartDrawerFooterUpdate) {
        cartDrawerFooter.innerHTML = cartDrawerFooterUpdate.innerHTML;
      }

      if (cartDrawerEmptyUpdate) {
        cartDrawerEmpty.innerHTML = cartDrawerEmptyUpdate.innerHTML;
      }

      if (event.cart.item_count > 0) {
        cartDrawerBody.classList.remove("hidden");
        cartDrawerFooter.classList.remove("hidden");
        cartDrawerEmpty.classList.add("hidden");
      } else {
        cartDrawerBody.classList.add("hidden");
        cartDrawerFooter.classList.add("hidden");
        cartDrawerEmpty.classList.remove("hidden");
      }
    }

    const mainCart = document.querySelector(`#MainCart-${sectionId}`);
    if (mainCart) {
      const updatedElement = sectionToRender.querySelector(
        `#MainCart-${sectionId}`
      );
      if (updatedElement) {
        mainCart.innerHTML = updatedElement.innerHTML;
      } else {
        mainCart.closest(".cart").classList.add("is-empty");
        mainCart.remove();
      }
    }

    const lineItem =
      document.getElementById(`CartItem-${event.line}`) ||
      document.getElementById(`CartDrawer-Item-${event.line}`);

    if (lineItem && lineItem.querySelector(`[name="${event.name}"]`)) {
      FoxTheme.a11y.trapFocus(
        mainCart || cartDrawer,
        lineItem.querySelector(`[name="${event.name}"]`)
      );
    } else if (event.cart.item_count === 0) {
      cartDrawer
        ? FoxTheme.a11y.trapFocus(cartDrawer, cartDrawer.querySelector("a"))
        : FoxTheme.a11y.trapFocus(
            document.querySelector(".cart__empty"),
            document.querySelector("a")
          );
    } else {
      cartDrawer
        ? FoxTheme.a11y.trapFocus(
            cartDrawer,
            cartDrawer.querySelector(".cart-item__title")
          )
        : FoxTheme.a11y.trapFocus(
            mainCart,
            mainCart.querySelector(".cart-item__title")
          );
    }

    document.dispatchEvent(
      new CustomEvent("cart:updated", {
        detail: {
          cart: event.cart,
        },
      })
    );
  }

  onCartError(errors, target) {
    if (target) {
      this.hideLoader(target.getAttribute("data-index"));
      this.setValidity(target, errors);
      return;
    } else {
      window.location.href = FoxTheme.routes.cart_url;
    }

    alert(errors);
  }

  updateQuantity(line, quantity, name, target) {
    this.showLoader(line);

    let sectionsToBundle = [];
    document.documentElement.dispatchEvent(
      new CustomEvent("cart:grouped-sections", {
        bubbles: true,
        detail: { sections: sectionsToBundle },
      })
    );

    const body = JSON.stringify({
      line,
      quantity,
      sections: sectionsToBundle,
    });

    fetch(`${FoxTheme.routes.cart_change_url}`, {
      ...FoxTheme.utils.fetchConfig(),
      ...{ body },
    })
      .then((response) => response.json())
      .then((parsedState) => {
        FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate, {
          sources: "cart-items",
          cart: parsedState,
          target,
          line,
          name,
        });
      })
      .catch((error) => {
        console.error("Error updating cart quantity:", error);
      });
  }

  showLoader(line) {
    const sectionId = FoxTheme.utils.getSectionId(this);
    const loaders = document.querySelectorAll(`#Loader-${sectionId}-${line}`);
    if (loaders) {
      loaders.forEach((loader) => {
        loader.classList.add("btn--loading");
      });
    }
  }

  hideLoader(line) {
    const sectionId = FoxTheme.utils.getSectionId(this);
    const loaders = document.querySelectorAll(`#Loader-${sectionId}-${line}`);
    if (loaders) {
      loaders.forEach((loader) => {
        loader.classList.remove("btn--loading");
      });
    }
  }

  setValidity(target, message) {
    target.setCustomValidity(message);
    target.reportValidity();
    target.value = target.defaultValue;
    target.select();
  }
}

customElements.define("cart-items", CartItems);

class CartRemoveItem extends HTMLAnchorElement {
  constructor() {
    super();

    this.addEventListener("click", (event) => {
      event.preventDefault();

      const cartItems = this.closest("cart-items");
      cartItems.updateQuantity(this.dataset.index, 0);
    });
  }
}
customElements.define("cart-remove-item", CartRemoveItem, { extends: "a" });

class CartAddonModal extends ModalComponent {
  constructor() {
    super();
  }

  /**
   * To avoid lost focus input elements
   */
  get focusElement() {
    return (
      this.querySelector("input, textarea, select") ||
      this.querySelector("button")
    );
  }
}
customElements.define("cart-addon-modal", CartAddonModal);

class CartAddonAccordion extends AccordionDetails {
  constructor() {
    super();
  }

  get focusElement() {
    return (
      this.querySelector("input, textarea, select") ||
      this.querySelector("button")
    );
  }

  handleAfterAnimated() {
    if (this.open) FoxTheme.a11y.trapFocus(this, this.focusElement);
  }
}
customElements.define("cart-addon-accordion", CartAddonAccordion, {
  extends: "details",
});

class CartNote extends HTMLElement {
  constructor() {
    super();

    this.addEventListener(
      "change",
      FoxTheme.utils.debounce(this.onChange.bind(this), 300)
    );

    if (this.button && this.cartNoteDetailsSummary) {
      this.button.addEventListener("click", () => {
        this.cartNoteDetailsSummary.close();
      });
    }
  }

  get cartNoteDetailsSummary() {
    return this.closest('[is="accordion-details"]');
  }

  get button() {
    return this.querySelector('[type="button"]');
  }

  onChange(event) {
    const body = JSON.stringify({ note: event.target.value });
    fetch(`${FoxTheme.routes.cart_update_url}`, {
      ...FoxTheme.utils.fetchConfig(),
      ...{ body },
    });
  }
}
customElements.define("cart-note", CartNote);
class CartDiscount extends HTMLFormElement {
  constructor() {
    super();

    this.addEventListener("submit", this.handleFormSubmit.bind(this));
  }

  get submitEl() {
    return (this._submitEl =
      this._submitEl || this.querySelector('[type="submit"]'));
  }

  get messageEl() {
    return (this._messageEl =
      this._messageEl || this.querySelector(".form__message"));
  }

  get couponEl() {
    return (this._couponEl =
      this._couponEl || this.querySelector('input[name="discount"]'));
  }

  get cartAddonDrawer() {
    return this.closest(".cart-addons-drawer");
  }

  get cartDiscountsEl() {
    return document.querySelector(".cart__discounts");
  }

  getDiscounts() {
    const discounts = [];

    if (this.cartDiscountsEl) {
      const items = this.cartDiscountsEl.querySelectorAll(".cart__discount");
      items &&
        items.forEach((item) => {
          discounts.push(item.dataset.discountCode);
        });
    }

    return discounts;
  }

  handleFormSubmit(event) {
    event.preventDefault();

    if (this.submitEl.getAttribute("aria-disabled") === "true") return;

    this.displayFormErrors();

    const newDiscountCode = this.couponEl.value;
    const discounts = this.getDiscounts();

    if (discounts.includes(newDiscountCode)) {
      this.displayFormErrors(FoxTheme.cartStrings.duplicateDiscountError);
      return;
    }

    discounts.push(newDiscountCode);

    let sectionsToBundle = [];
    document.documentElement.dispatchEvent(
      new CustomEvent("cart:grouped-sections", {
        bubbles: true,
        detail: { sections: sectionsToBundle },
      })
    );

    const config = FoxTheme.utils.fetchConfig("javascript");
    config.headers["X-Requested-With"] = "XMLHttpRequest";
    delete config.headers["Content-Type"];

    const formData = new FormData();
    formData.append("sections", sectionsToBundle);
    formData.append("sections_url", window.location.pathname);
    formData.append("discount", discounts.join(","));

    config.body = formData;

    this.submitEl.setAttribute("aria-disabled", "true");
    this.submitEl.classList.add("btn--loading");

    fetch(FoxTheme.routes.cart_update_url, config)
      .then((response) => response.json())
      .then(async (parsedState) => {
        if (
          parsedState.discount_codes.find(
            (
              /** @type {{ code: string; applicable: boolean; }} */ discount
            ) => {
              return (
                discount.code === newDiscountCode &&
                discount.applicable === false
              );
            }
          )
        ) {
          this.couponEl.value = "";
          this.displayFormErrors(FoxTheme.cartStrings.applyDiscountError);
          return;
        }

        if (this.cartAddonDrawer) {
          this.cartAddonDrawer.hide();
        }

        const cartJson = await (
          await fetch(`${FoxTheme.routes.cart_url}`, {
            ...FoxTheme.utils.fetchConfig(),
          })
        ).json();
        cartJson["sections"] = parsedState["sections"];

        this.updateCartState(cartJson);
      })
      .catch((e) => {
        console.error(e);
      })
      .finally(() => {
        this.submitEl.removeAttribute("aria-disabled");
        this.submitEl.classList.remove("btn--loading");
      });
  }

  updateCartState = (cartJson) => {
    FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate, {
      cart: cartJson,
    });
  };

  displayFormErrors = (errorMessage = false) => {
    if (!this.messageEl) {
      if (errorMessage !== false) {
        alert(errorMessage);
      }
    } else {
      this.messageEl.classList.toggle("!hidden", !errorMessage);
      if (errorMessage !== false) {
        this.messageEl.innerText = errorMessage;
      }
    }
  };
}
customElements.define("cart-discount", CartDiscount, { extends: "form" });

class CartDiscountRemove extends HTMLButtonElement {
  constructor() {
    super();

    this.selectors = {
      list: ".discounts",
      item: ".discount",
    };

    this.clickHandler = this.handleClick.bind(this);
  }

  connectedCallback() {
    this.listEl = this.closest(this.selectors.list);

    this.addEventListener("click", this.clickHandler);
  }

  disconnectedCallback() {
    this.removeEventListener("click", this.clickHandler);
  }

  handleClick(event) {
    event.preventDefault();
    if (this.getAttribute("aria-disabled") === "true") return;

    this.setAttribute("aria-disabled", "true");
    this.classList.add("btn--loading");

    this.discounts = [];

    const thisItem = this.closest("li");
    const items = this.listEl.querySelectorAll(this.selectors.item);
    items &&
      items.forEach((item) => {
        if (item != thisItem) {
          this.discounts.push(item.dataset.discountCode);
        }
      });

    this.updateCartDiscounts();
  }

  updateCartDiscounts() {
    let sectionsToBundle = [];
    document.documentElement.dispatchEvent(
      new CustomEvent("cart:grouped-sections", {
        bubbles: true,
        detail: { sections: sectionsToBundle },
      })
    );

    const config = FoxTheme.utils.fetchConfig("javascript");
    config.headers["X-Requested-With"] = "XMLHttpRequest";
    delete config.headers["Content-Type"];

    const formData = new FormData();
    formData.append("sections", sectionsToBundle);
    formData.append("sections_url", window.location.pathname);
    formData.append("discount", this.discounts.join(","));

    config.body = formData;

    fetch(FoxTheme.routes.cart_update_url, config)
      .then((response) => response.json())
      .then(async (parsedState) => {
        const cartJson = await (
          await fetch(`${FoxTheme.routes.cart_url}`, {
            ...FoxTheme.utils.fetchConfig(),
          })
        ).json();
        cartJson["sections"] = parsedState["sections"];

        this.updateCartState(cartJson);
      })
      .catch((e) => {
        console.error(e);
      });
  }

  updateCartState = (cartJson) => {
    FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate, {
      cart: cartJson,
    });
  };
}
customElements.define("cart-discount-remove", CartDiscountRemove, {
  extends: "button",
});

class CalculateShipping extends CartAddonModal {
  constructor() {
    super();
    this.countryProvince = this.querySelector("country-province");
    this.isCountrySetup = false;
  }
  static get observedAttributes() {
    return [...super.observedAttributes, "data-show"];
  }
  show() {
    super.show();
    this.setAttribute("data-show", true);
  }

  hide() {
    super.hide();
    this.setAttribute("data-show", false);
  }

  attributeChangedCallback(name, oldValue, newValue) {
    super.attributeChangedCallback(name, oldValue, newValue);
    if (
      name === "data-show" &&
      newValue === "true" &&
      !this.isCountrySetup &&
      this.countryProvince
    ) {
      const template = this.countryProvince.querySelector("template");
      const templateContent = document.importNode(template.content, true);
      this.countryProvince.countryElement.appendChild(templateContent);
      this.countryProvince.init();
      this.isCountrySetup = true;
    }
  }
}
customElements.define("calculate-shipping", CalculateShipping);
class CountryProvinceForm extends HTMLElement {
  constructor() {
    super();
    this.provinceElement = this.querySelector('[name="address[province]"]');
    this.countryElement = this.querySelector('[name="address[country]"]');
    this.template = this.dataset.template;
    this.countryElement.addEventListener(
      "change",
      this.handleCountryChange.bind(this)
    );
    if (this.template && this.template === "cart") {
      this.init();
    }
  }

  init() {
    if (this.getAttribute("country") !== "") {
      this.countryElement.selectedIndex = Math.max(
        0,
        Array.from(this.countryElement.options).findIndex(
          (option) => option.textContent === this.dataset.country
        )
      );
      this.countryElement.dispatchEvent(new Event("change"));
    } else {
      this.handleCountryChange();
    }
  }

  handleCountryChange() {
    const option =
        this.countryElement.options[this.countryElement.selectedIndex],
      provinces = JSON.parse(option.dataset.provinces);

    this.provinceElement.closest(".form-field").hidden = provinces.length === 0;

    if (provinces.length === 0) {
      return;
    }

    this.provinceElement.innerHTML = "";

    provinces.forEach((data) => {
      const selected = data[1] === this.dataset.province;
      this.provinceElement.options.add(
        new Option(data[1], data[0], selected, selected)
      );
    });
  }
}
customElements.define("country-province", CountryProvinceForm);

class ShippingCalculator extends HTMLFormElement {
  constructor() {
    super();

    this.submitButton = this.querySelector('[type="submit"]');
    this.resultsElement = this.lastElementChild;

    this.submitButton.addEventListener(
      "click",
      this.handleFormSubmit.bind(this)
    );
  }

  handleFormSubmit(event) {
    event.preventDefault();

    const zip = this.querySelector('[name="address[zip]"]').value,
      country = this.querySelector('[name="address[country]"]').value,
      province = this.querySelector('[name="address[province]"]').value;

    this.submitButton.classList.add("btn--loading");

    const body = JSON.stringify({
      shipping_address: { zip, country, province },
    });
    let sectionUrl = `${FoxTheme.routes.cart_url}/shipping_rates.json`;

    sectionUrl = sectionUrl.replace("//", "/");

    fetch(sectionUrl, {
      ...FoxTheme.utils.fetchConfig("javascript"),
      ...{ body },
    })
      .then((response) => response.json())
      .then((parsedState) => {
        if (parsedState.shipping_rates) {
          this.formatShippingRates(parsedState.shipping_rates);
        } else {
          this.formatError(parsedState);
        }
      })
      .catch((e) => {
        console.error(e);
      })
      .finally(() => {
        this.resultsElement.hidden = false;
        this.submitButton.classList.remove("btn--loading");
      });
  }

  formatError(errors) {
    const shippingRatesList = Object.keys(errors).map((errorKey) => {
      return `<li>${errors[errorKey]}</li>`;
    });
    this.resultsElement.innerHTML = `
      <div class="alert alert--error blocks-radius grid gap-2">
        <p class="font-body-bolder m-0">${
          FoxTheme.shippingCalculatorStrings.error
        }</p>
        <ul class="list-disc grid gap-1 text-sm" role="list">${shippingRatesList.join(
          ""
        )}</ul>
      </div>
    `;
  }

  formatShippingRates(shippingRates) {
    const shippingRatesList = shippingRates.map(
      ({ presentment_name, currency, price }) => {
        return `<li>${presentment_name}: ${currency} ${price}</li>`;
      }
    );
    this.resultsElement.innerHTML = `
      <div class="alert blocks-radius alert--${
        shippingRates.length === 0 ? "error" : "success"
      } grid gap-2 leading-tight">
        <p class="font-body-bolder m-0">${
          shippingRates.length === 0
            ? FoxTheme.shippingCalculatorStrings.notFound
            : shippingRates.length === 1
            ? FoxTheme.shippingCalculatorStrings.oneResult
            : FoxTheme.shippingCalculatorStrings.multipleResults
        }</p>
        ${
          shippingRatesList === ""
            ? ""
            : `<ul class="list-disc grid gap-1 text-sm" role="list">${shippingRatesList.join(
                ""
              )}</ul>`
        }
      </div>
    `;
  }
}
customElements.define("shipping-calculator", ShippingCalculator, {
  extends: "form",
});

class CartDrawerProductsRecommendation extends HTMLElement {
  constructor() {
    super();
    if ("requestIdleCallback" in window) {
      requestIdleCallback(this.init.bind(this), { timeout: 1500 });
    } else {
      const userAgent = navigator.userAgent;
      const isSafari =
        userAgent.includes("Safari") && !userAgent.includes("Chrome");
      if (isSafari) {
        this.init();
      } else {
        FoxTheme.Motion.inView(this, this.init.bind(this), {
          margin: "600px 0px 600px 0px",
        });
      }
    }
  }

  get slideContainer() {
    return this.querySelector(".swiper");
  }

  get sliderPagination() {
    return this.querySelector(".swiper-pagination");
  }

  get sliderNext() {
    return this.querySelector(".swiper-button-next");
  }

  get sliderPrev() {
    return this.querySelector(".swiper-button-prev");
  }

  init() {
    fetch(this.dataset.url)
      .then((response) => response.text())
      .then((responseText) => {
        const sectionInnerHTML = new DOMParser()
          .parseFromString(responseText, "text/html")
          .querySelector(".shopify-section");

        if (sectionInnerHTML === null) return;

        const recommendations = sectionInnerHTML.querySelector(
          "cart-drawer-products-recommendation"
        );
        if (recommendations && recommendations.innerHTML.trim().length) {
          const productCount =
            recommendations.querySelectorAll(".product-card");
          this.innerHTML = recommendations.innerHTML;
          this.initCarousel();
          if (productCount.length > 0) this.classList.remove("hidden");
          this.dispatchEvent(new CustomEvent("recommendations:loaded"));
        } else {
          this.closest(".shopify-section").remove();
          this.classList.add("hidden");
          this.dispatchEvent(new CustomEvent("is-empty"));
        }
      })
      .catch((e) => {
        console.error(e);
      });
  }

  initCarousel() {
    this.carousel = new FoxTheme.Carousel(this.slideContainer, {
      slidesPerView: 1,
      spaceBetween: 10,
      loop: false,
      pagination: {
        el: this.sliderPagination,
        type: "fraction",
      },
      navigation: {
        nextEl: this.sliderNext,
        prevEl: this.sliderPrev,
      },
    });

    this.carousel && this.carousel.init();
  }
}

customElements.define(
  "cart-drawer-products-recommendation",
  CartDrawerProductsRecommendation
);

class MainCart extends HTMLElement {
  constructor() {
    super();

    document.addEventListener(
      "cart:grouped-sections",
      this.getSectionToRender.bind(this)
    );
  }

  getSectionToRender(event) {
    event.detail.sections.push(FoxTheme.utils.getSectionId(this));
  }
}
customElements.define("main-cart", MainCart);

class FreeShippingGoal extends HTMLElement {
  constructor() {
    super();
    this.selectors = {
      leftToSpend: "[data-left-to-spend]",
    };
    this.goal =
      Number(this.dataset.minimumAmount) *
        Number(window.Shopify.currency.rate || 1) || 0;
    this.progress = this.querySelector("progress-bar");
    this.money_format = window.FoxTheme.settings.moneyFormat;
  }

  connectedCallback() {
    this.updateShippingGloal(Number(this.dataset.cartTotal));
    document.addEventListener("cart:updated", (event) => {
      this.updateShippingGloal(event.detail.cart.items_subtotal_price);
    });
  }

  updateShippingGloal(amount) {
    if (amount > 0) {
      this.classList.remove("hidden");
    } else {
      this.classList.add("hidden");
    }

    this.cartTotal = amount / 100;
    this.goalLeft = this.goal - this.cartTotal;
    this.goalDone = this.goalLeft <= 0;

    this.percent = (this.cartTotal * 100) / this.goal;

    if (this.percent >= 100) this.percent = 100;

    if (this.cartTotal >= this.goal) {
      this.progress.style.setProperty("--percent", `${this.percent}%`);
      this.classList.add("free-shipping-goal--done");
      this.progress.dataset.value = this.cartTotal;
      this.progress.dataset.max = this.goal;
    } else {
      let spend = (this.goal - this.cartTotal) * 100;
      this.querySelector(this.selectors.leftToSpend).innerHTML =
        FoxTheme.Currency.formatMoney(spend, this.money_format);
      this.classList.remove("free-shipping-goal--done");
      this.progress.style.setProperty("--percent", `${this.percent}%`);
      this.progress.dataset.value = this.cartTotal;
      this.progress.dataset.max = this.goal;
    }
  }
}
customElements.define("free-shipping-goal", FreeShippingGoal);

window.FoxKitAddToCart = async (payload) => {
  if (!payload?.properties?.["_FoxKit offer"]) return;

  const cartJson = await (
    await fetch(`${FoxTheme.routes.cart_url}`, {
      ...FoxTheme.utils.fetchConfig(),
    })
  ).json();
  cartJson["sections"] = payload["sections"];
  FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate, {
    cart: cartJson,
  });

  document.dispatchEvent(
    new CustomEvent("product-ajax:added", {
      detail: {
        product: payload,
      },
    })
  );
};

class GiftCardForm extends HTMLElement {
  constructor() {
    super();
    this.giftCards = [];
    this.checkoutUrl = null;
    this.totalDiscount = 0;
    this.cartTotal = 0;
  }

  connectedCallback() {
    this.inputEl = this.querySelector('input[name="gift_card_code"]');
    this.submitEl = this.querySelector('[is="gift-card-submit"]');
    this.messageEl = this.querySelector(".form__message");
    this.listContainer = this.querySelector(".gift-cards__list");
    this.listItems = this.querySelector(".gift-cards__items");

    this.loadGiftCards();
    this.updateDisplay();

    this.revalidateStoredGiftCards();

    document.addEventListener("cart:updated", (event) => {
      this.cartTotal = event.detail.cart.items_subtotal_price || 0;
      this.calculateDiscounts();
    });

    this.fetchCartTotal();
  }

  async revalidateStoredGiftCards() {
    if (this.giftCards.length === 0) {
      return;
    }

    const updatedCards = [];

    for (const card of this.giftCards) {
      try {
        const result = await this.validateGiftCard(card.code);

        if (result.valid && result.balance > 0) {
          updatedCards.push({
            code: result.code,
            balance: result.balance,
            appliedAmount: 0,
          });
        }
      } catch (error) {
        console.error(
          `[Gift Card] Error revalidating card ${card.code}:`,
          error
        );

        updatedCards.push(card);
      }
    }

    const hadCards = this.giftCards.length > 0;
    this.giftCards = updatedCards;
    this.saveGiftCards();

    if (this.cartTotal > 0) {
      this.calculateDiscounts();
    }
    this.updateDisplay();
    this.updateCartTotalDisplay();

    if (hadCards && this.giftCards.length === 0) {
      this.checkoutUrl = null;
      sessionStorage.removeItem("giftCardCheckoutUrl");
    }
  }

  async fetchCartTotal() {
    try {
      const response = await fetch(`${FoxTheme.routes.cart_url}`, {
        ...FoxTheme.utils.fetchConfig(),
      });
      const cart = await response.json();
      this.cartTotal = cart.items_subtotal_price || 0;
      this.calculateDiscounts();
      this.updateCartTotalDisplay();
    } catch (error) {
      console.error("Error fetching cart total:", error);
    }
  }

  loadGiftCards() {
    const stored = sessionStorage.getItem("giftCards");
    if (stored) {
      this.giftCards = JSON.parse(stored);
    }
    const storedCheckoutUrl = sessionStorage.getItem("giftCardCheckoutUrl");
    if (storedCheckoutUrl) {
      this.checkoutUrl = storedCheckoutUrl;
    }
  }

  saveGiftCards() {
    sessionStorage.setItem("giftCards", JSON.stringify(this.giftCards));
    if (this.checkoutUrl) {
      sessionStorage.setItem("giftCardCheckoutUrl", this.checkoutUrl);
    } else {
      sessionStorage.removeItem("giftCardCheckoutUrl");
    }
  }

  async validateGiftCard(code) {
    try {
      const response = await fetch(
        `https://app.aico.swiss/36367515/api/v1/subsidia-giftcards?giftCardCode=${encodeURIComponent(
          code
        )}`,
        {
          method: "GET",
          headers: {
            Authorization:
              "Bearer g9xzb3BgNtL8G9L7VvsELDo8IM7nPZE7Pbez62tsd06ef008",
            "Content-Type": "application/json",
          },
        }
      );

      const data = await response.json();

      if (response.status === 200) {
        return {
          valid: data.valid,
          balance: data.balance || 0,
          code: data.code || code,
          message: data.valid ? null : "Geschenkkarte ist abgelaufen",
        };
      } else if (response.status === 404) {
        return {
          valid: false,
          balance: 0,
          code: code,
          message: data.message || "Geschenkkarte existiert nicht",
        };
      } else {
        return {
          valid: false,
          balance: 0,
          code: code,
          message:
            data.message ||
            "Bei der Überprüfung der Geschenkkarte ist ein Fehler aufgetreten",
        };
      }
    } catch (error) {
      console.error("[Gift Card] Validation error:", error);
      return {
        valid: false,
        balance: 0,
        code: code,
        message:
          "Geschenkkarte konnte nicht überprüft werden. Bitte versuchen Sie es erneut.",
      };
    }
  }

  async applyGiftCard(code) {
    if (this.giftCards.find((card) => card.code === code)) {
      this.displayError("Diese Geschenkkarte wurde bereits eingelöst.");
      return;
    }

    const result = await this.validateGiftCard(code);

    if (!result.valid) {
      this.displayError(result.message || "Ungültiger Geschenkkarten-Code.");
      return;
    }

    const newCard = {
      code: result.code,
      balance: result.balance,
      appliedAmount: 0,
    };
    this.giftCards.push(newCard);

    this.saveGiftCards();
    this.inputEl.value = "";
    this.displayError(false);

    if (!this.cartTotal || this.cartTotal === 0) {
      await this.fetchCartTotal();
    } else {
      this.calculateDiscounts();
      this.updateDisplay();
      this.updateCartTotalDisplay();
    }

    await this.callGiftCardEndpoint();
  }

  calculateDiscounts() {
    let remainingTotal = this.cartTotal / 100;
    this.totalDiscount = 0;

    this.giftCards.forEach((card) => {
      const availableBalance = card.balance;
      const appliedAmount = Math.min(availableBalance, remainingTotal);
      card.appliedAmount = appliedAmount;
      this.totalDiscount += appliedAmount;
      remainingTotal -= appliedAmount;
    });

    this.updateDisplay();
    this.updateCartTotalDisplay();
  }

  async callGiftCardEndpoint() {
    const shopUrl = window.Shopify?.shop || window.location.hostname;
    const cartUrl = window.location.origin + FoxTheme.routes.cart_url;

    const body = {
      shop: shopUrl,
      giftCards: this.giftCards.map((card) => ({
        giftCard: card.code,
        applied_amount: card.appliedAmount,
      })),
      cart: cartUrl,
    };

    return new Promise((resolve) => {
      setTimeout(() => {
        const response = {
          checkoutUrl: null,
          draftOrderId: null,
          totalDiscount: this.totalDiscount,
          appliedCards: this.giftCards.map((card) => ({
            code: card.code,
            amount: card.appliedAmount,
          })),
          discountCode: this.giftCards.map((card) => card.code).join("+"),
        };

        if (response.checkoutUrl) {
          this.checkoutUrl = response.checkoutUrl;
          this.saveGiftCards();
        }

        resolve(response);
      }, 500);
    });
  }

  removeGiftCard(code) {
    this.giftCards = this.giftCards.filter((card) => card.code !== code);
    this.saveGiftCards();
    this.calculateDiscounts();
    this.updateDisplay();
    this.updateCartTotalDisplay();
    this.callGiftCardEndpoint();

    if (this.giftCards.length === 0) {
      this.checkoutUrl = null;
      this.saveGiftCards();
    }
  }

  updateDisplay() {
    if (this.giftCards.length === 0) {
      this.listContainer.classList.add("hidden");
      return;
    }

    this.listContainer.classList.remove("hidden");
    this.listItems.innerHTML = "";

    this.giftCards.forEach((card) => {
      const li = document.createElement("li");
      li.className = "discount cart__discount text-sm flex flex-col gap-2";

      const removeBtn = document.createElement("button");
      removeBtn.type = "button";
      removeBtn.className = "btn btn--inherit btn-remove discount__btn-remove";
      removeBtn.setAttribute("aria-label", `Remove gift card ${card.code}`);
      removeBtn.innerHTML = `
        <svg xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false" class="icon icon-close icon--extra-small" fill="none" viewBox="0 0 24 24">
          <path d="M18.75 5.13496L5.25 18.8544" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
          <path d="M18.75 18.8544L5.25 5.13496" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
      `;
      removeBtn.addEventListener("click", () => this.removeGiftCard(card.code));

      const codeSpan = document.createElement("span");
      codeSpan.textContent = `${FoxTheme.cartStrings.giftCardCodeLabel} ${card.code}`;

      const balanceSpan = document.createElement("span");
      balanceSpan.className = "font-body-bold";
      balanceSpan.textContent = `${
        FoxTheme.cartStrings.giftCardBalanceLabel
      } ${this.formatMoney(card.balance * 100)}`;

      li.appendChild(codeSpan);
      li.appendChild(balanceSpan);

      if (card.appliedAmount > 0) {
        const appliedSpan = document.createElement("span");
        appliedSpan.className = "font-body-bold";
        appliedSpan.textContent = `${
          FoxTheme.cartStrings.giftCardAppliedLabel
        } -${this.formatMoney(card.appliedAmount * 100)}`;
        li.appendChild(appliedSpan);
      }

      li.appendChild(removeBtn);
      this.listItems.appendChild(li);
    });
  }

  updateCartTotalDisplay() {
    const allSubtotalEls = document.querySelectorAll(".totals__subtotal-value");

    const existingGiftCardDiscounts = document.querySelectorAll(
      "[data-gift-card-discount]"
    );
    if (existingGiftCardDiscounts.length > 0) {
      existingGiftCardDiscounts.forEach((el) => el.remove());
    }

    const moneyFormat = window.FoxTheme?.settings?.moneyFormat || "${{amount}}";

    if (this.totalDiscount > 0) {
      const originalTotal = this.cartTotal;
      const discountedTotal = originalTotal - this.totalDiscount * 100;

      const formattedOriginal = FoxTheme.Currency.formatMoney(
        originalTotal,
        moneyFormat
      );
      const formattedDiscounted = FoxTheme.Currency.formatMoney(
        discountedTotal,
        moneyFormat
      );

      allSubtotalEls.forEach((subtotalEl, index) => {
        subtotalEl.innerHTML = `
          <span style="text-decoration: line-through; opacity: 0.6; margin-right: 0.5rem;">${formattedOriginal}</span>
          <span>${formattedDiscounted}</span>
        `;
      });
    } else {
      const formattedTotal = FoxTheme.Currency.formatMoney(
        this.cartTotal,
        moneyFormat
      );
      allSubtotalEls.forEach((subtotalEl) => {
        subtotalEl.textContent = formattedTotal;
      });
    }
  }

  formatMoney(cents) {
    const moneyFormat = window.FoxTheme?.settings?.moneyFormat || "${{amount}}";
    return FoxTheme.Currency.formatMoney(cents, moneyFormat);
  }

  displayError(message = false) {
    if (!this.messageEl) return;

    this.messageEl.classList.toggle("hidden", !message);
    if (message) {
      this.messageEl.innerHTML = `<p class="m-0">${message}</p>`;
    }
  }

  getCheckoutUrl() {
    return this.checkoutUrl;
  }

  async callDraftOrderEndpoint() {
    if (this.giftCards.length === 0) {
      return null;
    }

    let cartObject = null;
    try {
      const cartResponse = await fetch(`${FoxTheme.routes.cart_url}`, {
        ...FoxTheme.utils.fetchConfig(),
      });
      cartObject = await cartResponse.json();
    } catch (error) {
      console.error("[Gift Card] Error fetching cart object:", error);
      return null;
    }

    const shopUrl = window.Shopify?.shop || window.location.hostname;

    const body = {
      shop: shopUrl,
      giftCards: this.giftCards.map((card) => ({
        giftCard: card.code,
        applied_amount: card.appliedAmount,
      })),
      cart: cartObject,
    };

    try {
      const response = await fetch(
        "https://subsidia-giftcards.shopify-apps.aico.swiss/api/draft-order",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(body),
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const responseData = await response.json();

      if (responseData.checkoutUrl) {
        this.checkoutUrl = responseData.checkoutUrl;
        this.saveGiftCards();
      }

      return responseData;
    } catch (error) {
      console.error("[Gift Card] Error calling draft-order endpoint:", error);
      return null;
    }
  }
}

customElements.define("gift-card-form", GiftCardForm);

class GiftCardSubmit extends HTMLButtonElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.form = this.closest("gift-card-form");
    this.input = this.form?.querySelector('input[name="gift_card_code"]');

    if (!this.form) {
      console.error("[Gift Card] Gift card form not found!");
    }
    if (!this.input) {
      console.error("[Gift Card] Gift card input not found!");
    }

    this.addEventListener("click", this.handleClick.bind(this));
  }

  async handleClick(event) {
    event.preventDefault();
    event.stopPropagation();

    if (this.getAttribute("aria-disabled") === "true") {
      return;
    }
    if (!this.form || !this.input) {
      console.error("[Gift Card] Form or input not found in handleClick");
      return;
    }

    const code = this.input.value.trim();

    if (!code) {
      this.form.displayError("Please enter a gift card code.");
      return;
    }

    this.setAttribute("aria-disabled", "true");
    this.classList.add("btn--loading");

    try {
      await this.form.applyGiftCard(code);
    } catch (error) {
      console.error("[Gift Card] Error applying gift card:", error);
      this.form.displayError("An error occurred. Please try again.");
    } finally {
      this.removeAttribute("aria-disabled");
      this.classList.remove("btn--loading");
    }
  }
}

customElements.define("gift-card-submit", GiftCardSubmit, {
  extends: "button",
});

document.addEventListener("DOMContentLoaded", () => {
  const setupGiftCardButton = () => {
    const giftCardButtons = document.querySelectorAll(
      'button[is="gift-card-submit"]'
    );
    giftCardButtons.forEach((button) => {
      if (button.dataset.giftCardFallback) return;
      button.dataset.giftCardFallback = "true";

      button.addEventListener("click", async (event) => {
        event.preventDefault();
        event.stopPropagation();

        const form = button.closest("gift-card-form");
        const input = form?.querySelector('input[name="gift_card_code"]');

        if (!form || !input) {
          console.error(
            "[Gift Card] Form or input not found in fallback handler"
          );
          return;
        }

        if (button.getAttribute("aria-disabled") === "true") {
          return;
        }

        const code = input.value.trim();

        if (!code) {
          if (form.displayError) {
            form.displayError("Please enter a gift card code.");
          }
          return;
        }

        button.setAttribute("aria-disabled", "true");
        button.classList.add("btn--loading");

        try {
          if (form.applyGiftCard) {
            await form.applyGiftCard(code);
          } else {
            console.error("[Gift Card] applyGiftCard method not found on form");
          }
        } catch (error) {
          console.error(
            "[Gift Card] Error applying gift card via fallback:",
            error
          );
          if (form.displayError) {
            form.displayError("An error occurred. Please try again.");
          }
        } finally {
          button.removeAttribute("aria-disabled");
          button.classList.remove("btn--loading");
        }
      });
    });
  };

  setupGiftCardButton();

  document.addEventListener("cart:updated", () => {
    setTimeout(setupGiftCardButton, 100);
  });
});

document.addEventListener("DOMContentLoaded", () => {
  const setupCheckoutIntercept = () => {
    const checkoutButtons = document.querySelectorAll(
      'button[name="checkout"]'
    );
    const cartForm = document.getElementById("cart");

    checkoutButtons.forEach((button) => {
      if (button.dataset.giftCardIntercept) return;
      button.dataset.giftCardIntercept = "true";

      button.addEventListener("click", async (event) => {
        const giftCardForm = document.querySelector("gift-card-form");
        if (giftCardForm && giftCardForm.giftCards.length > 0) {
          event.preventDefault();
          event.stopPropagation();
          event.stopImmediatePropagation();

          button.setAttribute("aria-disabled", "true");
          button.classList.add("btn--loading");

          try {
            const response = await giftCardForm.callDraftOrderEndpoint();

            if (response && response.checkoutUrl) {
              window.location.href = response.checkoutUrl;
            } else {
              button.removeAttribute("aria-disabled");
              button.classList.remove("btn--loading");

              const form =
                button.closest("form") || document.getElementById("cart");
              if (form) {
                form.submit();
              }
            }
          } catch (error) {
            console.error("[Gift Card] Error during checkout:", error);

            button.removeAttribute("aria-disabled");
            button.classList.remove("btn--loading");

            const form =
              button.closest("form") || document.getElementById("cart");
            if (form) {
              form.submit();
            }
          }
          return false;
        }
      });
    });

    if (cartForm && !cartForm.dataset.giftCardIntercept) {
      cartForm.dataset.giftCardIntercept = "true";
      cartForm.addEventListener("submit", async (event) => {
        const submitter = event.submitter;
        if (submitter && submitter.name === "checkout") {
          const giftCardForm = document.querySelector("gift-card-form");
          if (giftCardForm && giftCardForm.giftCards.length > 0) {
            event.preventDefault();
            event.stopPropagation();

            if (submitter) {
              submitter.setAttribute("aria-disabled", "true");
              submitter.classList.add("btn--loading");
            }

            try {
              const response = await giftCardForm.callDraftOrderEndpoint();

              if (response && response.checkoutUrl) {
                window.location.href = response.checkoutUrl;
              } else {
                if (submitter) {
                  submitter.removeAttribute("aria-disabled");
                  submitter.classList.remove("btn--loading");
                }

                cartForm.submit();
              }
            } catch (error) {
              console.error("[Gift Card] Error during form submit:", error);

              if (submitter) {
                submitter.removeAttribute("aria-disabled");
                submitter.classList.remove("btn--loading");
              }

              cartForm.submit();
            }
            return false;
          } else {
            console.error(
              "[Gift Card] No gift cards, proceeding with normal checkout"
            );
          }
        }
      });
    }
  };

  setupCheckoutIntercept();

  document.addEventListener("cart:updated", () => {
    setTimeout(setupCheckoutIntercept, 100);
  });
});
